// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Highscore from "./highscore.js"
import Organization from "./organization.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "HighscoreCommune",
  "collectionKey": "highscore_communes",
  "collectionName": "highscore_communes",
  "i18nKey": "highscore_commune",
  "camelizedLower": "highscoreCommune",
  "name": "HighscoreCommune",
  "nameDasherized": "highscore-commune",
  "pluralName": "highscore_communes",
  "ransackable_associations": [
    {
      "className": "Organization",
      "collectionName": "organizations",
      "foreignKey": "commune_id",
      "name": "commune",
      "macro": "belongs_to",
      "resource_name": "Organization",
      "through": null
    },
    {
      "className": "Highscore",
      "collectionName": "highscores",
      "foreignKey": "highscore_id",
      "name": "highscore",
      "macro": "belongs_to",
      "resource_name": "Highscore",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "commune_id",
      "column": {
        "default": null,
        "name": "commune_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "highscore_id",
      "column": {
        "default": null,
        "name": "highscore_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_commune_id",
      "column": {
        "default": null,
        "name": "old_commune_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_highscore_id",
      "column": {
        "default": null,
        "name": "old_highscore_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Organization",
      "collectionName": "organizations",
      "foreignKey": "commune_id",
      "name": "commune",
      "macro": "belongs_to",
      "resource_name": "Organization",
      "through": null
    },
    {
      "className": "Highscore",
      "collectionName": "highscores",
      "foreignKey": "highscore_id",
      "name": "highscore",
      "macro": "belongs_to",
      "resource_name": "Highscore",
      "through": null
    }
  ],
  "paramKey": "highscore_commune",
  "primaryKey": "id"
}

/** Frontend model for HighscoreCommune. */
export default class HighscoreCommune extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {import("./organization.js").default | null} */
  commune() {
    return this._readBelongsToReflection({modelClass: Organization, reflectionName: "commune"})
  }

  /** @returns {Promise<import("./organization.js").default | null>} */
  loadCommune() {
    if (!("communeId" in this)) throw new Error("Foreign key method wasn't defined: communeId")

    const id = this.communeId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "commune",
        model: this,
        modelClass: Organization
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./organization.js").default | null>} */
  ensureCommuneLoaded() {
    return this.ensureAssociationLoaded("commune")
  }

  /** @returns {import("./highscore.js").default | null} */
  highscore() {
    return this._readBelongsToReflection({modelClass: Highscore, reflectionName: "highscore"})
  }

  /** @returns {Promise<import("./highscore.js").default | null>} */
  loadHighscore() {
    if (!("highscoreId" in this)) throw new Error("Foreign key method wasn't defined: highscoreId")

    const id = this.highscoreId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "highscore",
        model: this,
        modelClass: Highscore
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./highscore.js").default | null>} */
  ensureHighscoreLoaded() {
    return this.ensureAssociationLoaded("highscore")
  }
}
